import axios from "axios";
import styled from 'styled-components';
import DeleteIcon from 'mdi-react/DeleteIcon';
import { Form, Field } from 'react-final-form';
import { Button } from '@/shared/components/Button';
import AdminInterface from '@/utils/AdminInterface';
import React, { useRef, useState, useEffect } from "react";
import { paddingLeft, paddingRight } from '@/utils/directions';
import { FileInputField } from "@/shared/components/form/FileInput";
import renderComponentField from '@/shared/components/form/FormField';
import ReactTableBase from '@/shared/components/table/ReactTableBase';
import { colorAdditional, colorGreenHover, colorAccent, colorRed } from '@/utils/palette';
import { Card, CardBody, CardTitleWrap, CardTitle, CardSubhead } from '@/shared/components/Card';
import { FormButtonToolbar, FormContainer, FormGroup, FormGroupField, FormGroupLabel } from '@/shared/components/form/FormElements';

const PARTS_SIGN_BATCH_SIZE = 100;
const CONCURRENCY = 4;

const CartTableButton = styled.button`
  background: transparent;
  border: none;
  line-height: 14px;
  ${paddingLeft}: 20px;
  ${paddingRight}: 0;
  padding-top: 0;
  padding-bottom: 0;
  color: ${colorAdditional};
  cursor: pointer;
  transition: all 0.3s;

  svg {
    height: 20px;
    width: 20px;
    fill: ${colorAdditional};
    transition: all 0.3s;
  }

  &:hover svg {
    fill: ${colorGreenHover};
  }

  &:hover svg.orange-hover {
    fill: #e9c886;
  }

  &:hover svg.red-hover {
    fill: #e98686;
  }
`;

const ProgressBar = styled.progress`
  width: 100%;
  height: 16px;
  border-radius: 4px;
  overflow: hidden;
  background-color: #e9ecef;
  border: none;

  &::-webkit-progress-bar {
    background-color: #e9ecef;
    border-radius: 4px;
  }

  &::-webkit-progress-value {
    background-color: ${colorAccent};
    border-radius: 4px;
    transition: width 0.2s;
  }

  &::-moz-progress-bar {
    background-color: ${colorAccent};
    border-radius: 4px;
    transition: width 0.2s;
  }

  &::-ms-fill {
    background-color: ${colorAccent};
    border-radius: 4px;
    transition: width 0.2s;
  }
`;

const UploadFiles = () => {
  const [files, setFiles] = useState([]);
  const [progresses, setProgresses] = useState({});
  const [statuses, setStatuses] = useState({});
  const [errors, setErrors] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const uploadIdRefs = useRef({});
  const isCancelledRef = useRef({});
  const [filesList, setFilesList] = useState({ tableHeaderData: [], tableRowsData: [] });

  const tableConfig = {
    isEditable: false,
    isSortable: false,
    isResizable: false,
    withPagination: false,
  };

  const getStatus = (file) => {
    return (
    <div style={{ width: 200, marginLeft: 20 }}>
      <ProgressBar value={progresses[file.name] ?? 0} max={100} style={{ width: "80%" }}/>
      <div style={{ fontSize: "12px", color: statuses[file.name] === "Failed" ? colorRed : statuses[file.name] === "Completed" ? colorAccent : "#555" }}>
        {statuses[file.name] || "Idle"}
        {errors[file.name] &&
          <span style={{ marginLeft: 12, color: colorRed }}>
            {errors[file.name]}
          </span>
        }
      </div>
    </div>)
  }

  useEffect(() => {
    const columns = [
      {
          accessor: 'filename',
          Header: 'File',
      },
      {
        accessor: 'size',
        Header: 'Size (MB)',
      },
      {
          accessor: 'delete',
          Header: 'Delete',
          disableGlobalFilter: true,
          disableSortBy: true,
      },
      {
          accessor: 'status',
          Header: 'Status',
          disableGlobalFilter: true,
          disableSortBy: true,
      }
    ];

    const data = [];
    files.forEach(file => {
        data.push({
          filename: file.name,
          size: Math.round((file.size / (1024 * 1024)) * 10) / 10,
          delete: <CartTableButton type="button" onClick={() => onDelete(file.name)}><DeleteIcon className="red-hover"/></CartTableButton>,
          status: getStatus(file)
        });
    });
    setFilesList({ tableHeaderData: columns, tableRowsData: data });
  }, [files, progresses, statuses, errors]);

  const onFileChange = (e) => {
    let selected = [...e.target.files];
    setFiles(prev => {
      const existingNames = prev.map(f => f.name);
      const unique = selected.filter(f => !existingNames.includes(f.name));
      return [...prev, ...unique];
    });
    e.target.value = null;
  };

  const onDelete = (name) => {
    setFiles((prev) => prev.filter((f) => f.name !== name));
    setProgresses((prev) => {
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
    setStatuses((prev) => {
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
    setErrors((prev) => {
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
    delete uploadIdRefs.current[name];
    delete isCancelledRef.current[name];
  };

  const onCancelAll = async () => {
    setIsUploading(false);
    files.forEach(f => {
      isCancelledRef.current[f.name] = true;
    });
    setStatuses((prev) => {
      const next = { ...prev };
      files.forEach(f => { next[f.name] = "Cancelled"; });
      return next;
    });

    await Promise.all(
      files.map(async (file) => {
        const uploadId = uploadIdRefs.current[file.name];
        if (uploadId) {
            await AdminInterface.instance.abortUpload(uploadId);
        }
      })
    );
  };

  const onUploadAll = async (form) => {
    const folder_name = form && form.folder_name ? form.folder_name : '';
    setIsUploading(true);
    setErrors({});
    setStatuses((prev) => {
      const copy = { ...prev };
      files.forEach(f => {
        copy[f.name] = "Queued";
      });
      return copy;
    });

    for (const file of files) {
      isCancelledRef.current[file.name] = false;
    }

    await Promise.all(
      files.map(async (file) => {
        setStatuses(prev => ({ ...prev, [file.name]: "Starting..." }));
        setProgresses(prev => ({ ...prev, [file.name]: 0 }));
        try {
          await multipartUpload(file, folder_name);
          setStatuses(prev => ({ ...prev, [file.name]: "Completed" }));
          setProgresses(prev => ({ ...prev, [file.name]: 100 }));
        } catch (e) {
          setErrors(prev => ({ ...prev, [file.name]: e.message || "Upload failed" }));
          setStatuses(prev => ({ ...prev, [file.name]: "Failed" }));
        }
      })
    );

    setIsUploading(false);
  };

  async function multipartUpload(file, folder_name) {
    setStatuses(prev => ({ ...prev, [file.name]: "Initializing..." }));
    let request_data = { 
      file_name: file.name, 
      folder_name: folder_name,
      content_type: 'application/octet-stream', 
      total_size_bytes: file.size 
    }
    const response = await AdminInterface.instance.initUpload(request_data);
    const uploadId = response.data.upload_id;
    const partSize = response.data.part_size_bytes;
    const totalParts = Math.ceil(file.size / partSize);
    uploadIdRefs.current[file.name] = uploadId;
    const completedParts = [];
    let uploadedBytes = 0;

    for (let partStart = 1; partStart <= totalParts; partStart += PARTS_SIGN_BATCH_SIZE) {
      if (isCancelledRef.current[file.name]) throw new Error("Upload cancelled by user.");

      const partEnd = Math.min(totalParts, partStart + PARTS_SIGN_BATCH_SIZE - 1);
      const partNumbers = [];
      for (let p = partStart; p <= partEnd; p++) partNumbers.push(p);

      setStatuses(prev => ({ ...prev, [file.name]: `Signing parts ${partStart}-${partEnd}...` }));
      const response = await AdminInterface.instance.signUploadParts(uploadId, partNumbers);
      const signed = response.data;

      const queue = [...signed.parts];
      const workers = Array.from({ length: CONCURRENCY }, async () => {
        while (queue.length > 0) {
          if (isCancelledRef.current[file.name]) throw new Error("Upload cancelled by user.");

          const part = queue.shift();
          if (!part) return;

          const partNumber = part.part_number;
          const start = (partNumber - 1) * partSize;
          const end = Math.min(start + partSize, file.size);
          const chunk = file.slice(start, end);

          setStatuses(prev => ({ ...prev, [file.name]: `Uploading part ${partNumber}/${totalParts}...` }));

          let putRes;
          try {
            putRes = await axios.put(part.url, chunk, { validateStatus: null });
          } catch (err) {
            throw new Error(`Network error for part ${partNumber}`);
          }
          
          if (putRes.status !== 200)
            throw new Error(`Part ${partNumber} upload failed (${putRes.status})`);

          const etag = putRes.headers["etag"] || putRes.headers["ETag"];     
          if (!etag) {
            throw new Error(`Missing ETag for part ${partNumber}`);
          }
          completedParts.push({
            part_number: partNumber,
            etag: etag.replaceAll('"', ""),
          });

          uploadedBytes += chunk.size;
          setProgresses(prev => ({
            ...prev,
            [file.name]: Math.round((uploadedBytes / file.size) * 100)
          }));
        }
      });
      await Promise.all(workers);
    }

    if (isCancelledRef.current[file.name]) throw new Error("Upload cancelled by user.");

    completedParts.sort((a, b) => a.part_number - b.part_number);
    setStatuses(prev => ({ ...prev, [file.name]: "Completing upload..." }));

    const complete_response = await AdminInterface.instance.completeUpload(uploadId, completedParts);
    return complete_response.data;
  }

  return (
    <Card>
      <CardBody>
        <CardTitleWrap>
          <CardTitle>Upload Files</CardTitle>
          <CardSubhead>Upload files into AWS S3</CardSubhead>
        </CardTitleWrap>
        <Form onSubmit={onUploadAll}>
          {({ handleSubmit, form }) => (
              <FormContainer horizontal onSubmit={handleSubmit}>
                <FormGroup>
                    <FormGroupLabel>Select Files to Upload</FormGroupLabel>
                    <FormGroupField>
                      <FileInputField multiple onChange={onFileChange} name='upload'/>
                      {files.length === 0 &&
                        <span style={{ color: "#888", textAlign: "center", paddingLeft: 16, paddingTop: 10 }}>No files selected.</span>
                      }
                    </FormGroupField>
                </FormGroup>
                <FormGroup>
                    <FormGroupLabel>Folder Name (Optional)</FormGroupLabel>
                    <FormGroupField>
                        <Field name="folder_name" component={renderComponentField} placeholder="Folder Name" style={{width: '50%'}} />
                    </FormGroupField>
                </FormGroup>
                <FormButtonToolbar>
                    <Button variant="primary" type="submit" disabled={isUploading || files.length === 0}>Upload</Button>
                    <Button variant="warning" type="button" disabled={!isUploading} onClick={onCancelAll}>Cancel Upload</Button>
                </FormButtonToolbar>
              </FormContainer>
          )}
        </Form>
        <ReactTableBase columns={filesList.tableHeaderData} data={filesList.tableRowsData} tableConfig={tableConfig} />
      </CardBody>
    </Card>
  );
}

export default UploadFiles;